import { Specialization } from '@/types/consultant-registration.types';
import { FormField } from '@/components/broker-registration/FormField';
import { SelectInput } from '@/components/broker-registration/SelectInput';
import {
  INDUSTRIES,
  EXPERIENCE_YEARS_OPTIONS,
  COMMON_EXPERTISE_OPTIONS,
  EXPERTISE_GROUPS,
  INDUSTRY_EXPERTISE_MAPPING,
} from '@/constants/consultant-registration.constants';
import { useLanguage } from '@/contexts/LanguageContext';

interface SpecializationBlockProps {
  specialization: Specialization;
  index: number;
  onUpdate: (index: number, updates: Partial<Specialization>) => void;
  onRemove: (index: number) => void;
  errors: Record<string, string>;
  clearError: (field: string) => void;
  canRemove: boolean;
}

export function SpecializationBlock({
  specialization,
  index,
  onUpdate,
  onRemove,
  errors,
  clearError,
  canRemove,
}: SpecializationBlockProps) {
  const { t } = useLanguage();

  // Build available expertises for selected industry
  const availableExpertises = () => {
    if (!specialization.industry) return [];

    const groups = INDUSTRY_EXPERTISE_MAPPING[specialization.industry] || [];
    let expertises: string[] = [...COMMON_EXPERTISE_OPTIONS];

    groups.forEach((group) => {
      if (EXPERTISE_GROUPS[group]) {
        expertises = expertises.concat(EXPERTISE_GROUPS[group]);
      }
    });

    return expertises;
  };

  const industryOptions = INDUSTRIES.map((industry) => ({
    value: industry.value,
    label: t(`consultant_registration.options.industries.${industry.value}`),
  }));

  const experienceOptions = EXPERIENCE_YEARS_OPTIONS.map((exp) => ({
    value: exp.value,
    label: t(`consultant_registration.options.experience_years.${exp.value}`),
  }));

  const toggleExpertise = (expertise: string) => {
    const currentExpertises = specialization.expertises || [];
    const newExpertises = currentExpertises.includes(expertise)
      ? currentExpertises.filter((e) => e !== expertise)
      : [...currentExpertises, expertise];

    onUpdate(index, { expertises: newExpertises });
    clearError(`spec${index}_expertises`);
  };

  return (
    <div className="border-2 border-[#CEAB65]/30 rounded-lg p-4 mb-4 bg-gray-700/50">
      <div className="flex justify-between items-center mb-3">
        <h4 className="text-lg font-semibold text-white">
          {t('consultant_registration.step_two.sector')} {index + 1}
        </h4>
        {canRemove && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className="text-sm bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 transition-colors"
          >
            {t('consultant_registration.step_two.remove')}
          </button>
        )}
      </div>

      <div className="space-y-4">
        <FormField
          label={t('consultant_registration.step_two.business_sector')}
          error={errors[`spec${index}_industry`]}
          required
        >
          <SelectInput
            value={specialization.industry}
            onChange={(value) => {
              onUpdate(index, { industry: value, expertises: [] });
              clearError(`spec${index}_industry`);
            }}
            options={[
              { value: '', label: t('consultant_registration.step_two.select_sector') },
              ...industryOptions,
            ]}
            error={!!errors[`spec${index}_industry`]}
          />
        </FormField>

        {specialization.industry && availableExpertises().length > 0 && (
          <>
            <FormField
              label={t('consultant_registration.step_two.key_expertises')}
              error={errors[`spec${index}_expertises`]}
              required
            >
              <p className="text-xs text-gray-400 mb-2">
                {t('consultant_registration.step_two.key_expertises_hint')}
              </p>
              <div
                className="grid grid-cols-1 gap-2 p-3 rounded border bg-gray-700 max-h-48 overflow-y-auto"
                data-error={!!errors[`spec${index}_expertises`]}
              >
                {availableExpertises().map((expertise) => (
                  <label
                    key={expertise}
                    className="flex items-start gap-2 text-sm text-white/95 cursor-pointer hover:bg-white/5 p-2 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={specialization.expertises.includes(expertise)}
                      onChange={() => toggleExpertise(expertise)}
                      className="mt-1 rounded text-[#CEAB65] flex-shrink-0"
                    />
                    <span>{t(`consultant_registration.options.expertises.${expertise}`)}</span>
                  </label>
                ))}
              </div>
            </FormField>

            <FormField
              label={t('consultant_registration.step_two.years_experience')}
              error={errors[`spec${index}_experience`]}
              required
            >
              <SelectInput
                value={specialization.experienceYears}
                onChange={(value) => {
                  onUpdate(index, { experienceYears: value as any });
                  clearError(`spec${index}_experience`);
                }}
                options={[
                  { value: '', label: t('consultant_registration.step_two.select_experience') },
                  ...experienceOptions,
                ]}
                error={!!errors[`spec${index}_experience`]}
              />
            </FormField>
          </>
        )}
      </div>
    </div>
  );
}
